import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import type { NotificationStreamItem } from "@situate/contracts";

import { ConsoleSurface, SeverityAccent, StatusBadge, getSeverityAccentColor } from "@/shared/ui";
import { formatNotificationCreatedAt } from "@/features/deployment-dashboard/notification-center/notification-stream-item-format";

interface NotificationPopupCardProps {
  item: NotificationStreamItem;
  onClick: () => void;
  onDismiss: () => void;
  durationMs: number;
}

/**
 * Top-right popup card for a single newly added notification. Shows only
 * contract fields (title, severity, raised time, and an optional scope
 * identifier) -- no invented location/category/metrics. Clicking anywhere on
 * the card dismisses it and hands control back to the caller to open the bell
 * panel with this notification selected.
 */
export function NotificationPopupCard({
  item,
  onClick,
  onDismiss,
  durationMs,
}: NotificationPopupCardProps) {
  const theme = useTheme();
  const stripeColor = getSeverityAccentColor(theme, item.severity);
  const [remainingPercent, setRemainingPercent] = useState(100);

  useEffect(() => {
    const startedAt = Date.now();
    const updateProgress = () => {
      const elapsed = Date.now() - startedAt;
      setRemainingPercent(Math.max(0, 100 - (elapsed / durationMs) * 100));
    };
    const intervalId = setInterval(updateProgress, 100);
    return () => clearInterval(intervalId);
  }, [durationMs]);

  return (
    <Box sx={{ position: "relative", width: 320 }}>
      <ButtonBase
        onClick={onClick}
        aria-label={`Open notification: ${item.title}`}
        sx={(buttonTheme) => ({
          display: "block",
          width: "100%",
          textAlign: "left",
          borderRadius: `${buttonTheme.shape.borderRadius}px`,
        })}
      >
        <ConsoleSurface
          sx={{
            px: 2,
            py: 1.5,
            pr: 6,
            width: "100%",
            backgroundColor: theme.palette.background.paper,
            borderLeft: `4px solid ${stripeColor}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.35)}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              pr: 1,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
            }}
          >
            {item.title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              pr: 1,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 3,
              overflow: "hidden",
            }}
          >
            {item.message}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {formatNotificationCreatedAt(item.created_at)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1 }}>
            {item.type === "alert" && <StatusBadge severity="active" label="Alert" />}
            <SeverityAccent level={item.severity} label={item.severity} />
          </Box>
        </ConsoleSurface>
      </ButtonBase>
      <IconButton
        aria-label="Dismiss notification"
        onClick={(event) => {
          event.stopPropagation();
          onDismiss();
        }}
        size="small"
        sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary" }}
      >
        <CircularProgress
          aria-label={`${Math.ceil(remainingPercent)}% of notification display time remaining`}
          variant="determinate"
          value={remainingPercent}
          size={26}
          thickness={4}
          sx={{ color: stripeColor }}
        />
        <CloseIcon sx={{ position: "absolute", fontSize: "0.9rem" }} />
      </IconButton>
    </Box>
  );
}
